/* =====================================================================================
   A select you can type into.

   A native <select> does have keyboard matching, but it matches only from the start of the
   label and it resets between keystrokes, so with 78 countries in the list the practical way
   to reach Switzerland is to scroll. This replaces it with a filter box that narrows as you
   type, which is the behaviour people already expect from every other search field in Forum.

   MATCHING IS ACCENT-INSENSITIVE AND SUBSTRING-WIDE, deliberately. A list of countries is
   exactly where diacritics bite: nobody types the umlaut in Türkiye or the accent in Curaçao,
   and somebody looking for "Korea" should find both Koreas without knowing whether the list
   spells one of them "Republic of". Prefix matches still rank above interior ones, so typing
   "ind" puts India above Finland.

   KEYBOARD FIRST. Up and down move the highlight, Enter takes it, Escape abandons the edit and
   restores what was selected. Blurring does the same rather than leaving half-typed text in a
   control that is meant to hold a choice, which is the usual failure of a hand-rolled combobox.
   ===================================================================================== */

import { useEffect, useMemo, useRef, useState } from 'react';
import './combo.css';

export interface ComboOption { value: string; label: string; hint?: string }

/** Lowercase, strip diacritics. "Türkiye" and "Turkiye" must be the same needle. */
const fold = (s: string) =>
  s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export function Combo({
  value, onChange, options, placeholder = 'Search', ariaLabel, width,
}: {
  value: string;
  onChange: (v: string) => void;
  options: ComboOption[];
  placeholder?: string;
  ariaLabel?: string;
  width?: number;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  const listId = useRef(`combo-${Math.random().toString(36).slice(2, 8)}`).current;

  const selected = options.find((o) => o.value === value);

  const shown = useMemo(() => {
    const n = fold(q.trim());
    if (!n) return options;
    return options
      .map((o) => {
        const hay = fold(o.label);
        const at = hay.indexOf(n);
        if (at < 0) return null;
        // A prefix match is what the person almost always meant.
        return { o, rank: at === 0 ? 0 : 1, at };
      })
      .filter(Boolean)
      .sort((a, b) => a!.rank - b!.rank || a!.at - b!.at
                      || a!.o.label.localeCompare(b!.o.label))
      .map((x) => x!.o);
  }, [q, options]);

  // Keep the highlight inside the list as it narrows.
  useEffect(() => { setActive(0); }, [q]);

  useEffect(() => {
    if (!open) return;
    function away(e: MouseEvent) {
      if (box.current && !box.current.contains(e.target as Node)) close();
    }
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  function close() { setOpen(false); setQ(''); }

  function take(v: string) { onChange(v); close(); }

  function onKey(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      setActive((i) => {
        const n = shown.length;
        if (!n) return 0;
        return e.key === 'ArrowDown' ? (i + 1) % n : (i - 1 + n) % n;
      });
      return;
    }
    if (e.key === 'Enter') {
      if (open && shown[active]) { e.preventDefault(); take(shown[active].value); }
      return;
    }
    if (e.key === 'Escape') { e.preventDefault(); close(); }
  }

  return (
    <div className="combo" ref={box} style={width ? { width } : undefined}>
      <input
        className="input combo-in"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-label={ariaLabel}
        placeholder={selected ? selected.label : placeholder}
        value={open ? q : (selected?.label ?? '')}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
      />
      {open && (
        <ul className="combo-list" id={listId} role="listbox">
          {shown.length === 0 && <li className="combo-none">No match</li>}
          {shown.map((o, i) => (
            <li
              key={o.value}
              role="option"
              aria-selected={o.value === value}
              className={`combo-opt${i === active ? ' on' : ''}${o.value === value ? ' sel' : ''}`}
              /* mousedown, not click: the input's blur would otherwise close the list before
                 the click landed, which is the classic reason a dropdown "does not select". */
              onMouseDown={(e) => { e.preventDefault(); take(o.value); }}
              onMouseEnter={() => setActive(i)}
            >
              <span>{o.label}</span>
              {o.hint && <span className="combo-hint">{o.hint}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
